import {
  AssignmentStartBuildResult, AssignmentStartFormValue, assignmentStartFormFromParameters,
  assignmentStartKind, buildAssignmentStartParameters,
} from './assignment-start.util';
import { normalizeMonthlyProgressParameters, parameterFieldSchema } from './assignment-parameter-fields.util';
import { mergeProcessParameters, parameterKey, parseParameterDefaults } from './process-parameter-defaults.util';

export interface AssignmentDefaultsState {
  inherited: Record<string, unknown>;
  explicit: Record<string, unknown>;
  value: AssignmentStartFormValue;
  initialBuilt: Record<string, unknown>;
}

export function prepareAssignmentDefaults(processKey: string, defaults: Record<string, unknown>, seedJson: string | null,
  seedIsTemplate = false): AssignmentDefaultsState {
  const parsed = parseParameterDefaults(seedJson?.trim() || '{}');
  if (!parsed.ok) throw new Error(parsed.error);
  const inherited = normalizeMonthlyProgressParameters(processKey, defaults);
  const seed = normalizeMonthlyProgressParameters(processKey, parsed.parameters);
  const explicit = seedIsTemplate ? {} : seed;
  const displayed = seedIsTemplate
    ? mergeProcessParameters(seed, inherited)
    : mergeProcessParameters(inherited, explicit);
  const value = assignmentStartFormFromParameters(processKey, projectKnownKeys(processKey, displayed));
  const initial = buildAssignmentStartParameters(processKey, value);
  return { inherited: structuredClone(inherited), explicit: structuredClone(explicit), value,
    initialBuilt: initial.ok ? structuredClone(initial.parameters) : {} };
}

function projectKnownKeys(processKey: string, source: Record<string, unknown>): Record<string, unknown> {
  if (assignmentStartKind(processKey) === 'json') return source;
  const names = ['name', 'geometry', 'sourceType', 'regionIds', 'countryName', 'geoJson', 'geoJsonFileId',
    'startDate', 'endDate', 'providers', 'priority', 'maximumCloudCover', 'satelliteProductId', 'fieldId',
    'dateFrom', 'dateTo', 'force', 'polygonDetectionJobId', 'take', 'failEvery', 'maxReportsPerRun',
    'alsoPdf', 'locale', 'bucket', 'fromUtc', 'toUtc', 'points', 'lat', 'lng'];
  const canonical = new Map(names.map(name => [name.toLowerCase(), name]));
  for (const field of parameterFieldSchema(processKey) ?? []) canonical.set(field.name.toLowerCase(), field.name);
  const project = (input: Record<string, unknown>): Record<string, unknown> => Object.fromEntries(
    Object.entries(input).map(([key, value]) => [canonical.get(key.toLowerCase()) ?? key, value]));
  const result = project(source);
  if (result['geometry'] && typeof result['geometry'] === 'object' && !Array.isArray(result['geometry'])) {
    result['geometry'] = project(result['geometry'] as Record<string, unknown>);
  }
  if (Array.isArray(result['points'])) {
    result['points'] = result['points'].map(point => point && typeof point === 'object' && !Array.isArray(point)
      ? project(point as Record<string, unknown>) : point);
  }
  return result;
}

export function buildAssignmentWithDefaults(processKey: string, state: AssignmentDefaultsState, value: AssignmentStartFormValue,
  editedFields: ReadonlySet<string>, jsonEdited = false): AssignmentStartBuildResult {
  const built = buildAssignmentStartParameters(processKey, value);
  if (!built.ok) return built;
  if (assignmentStartKind(processKey) === 'json') {
    const parsed = parseParameterDefaults(value.parametersJson);
    if (!parsed.ok) return parsed;
  }
  const wholeJson = assignmentStartKind(processKey) === 'json' && jsonEdited;
  let result = wholeJson ? structuredClone(built.parameters) : structuredClone(state.explicit);
  if (!wholeJson) {
    for (const [key, current] of Object.entries(built.parameters)) {
      const initialKey = parameterKey(state.initialBuilt, key);
      const changed = editedFields.has(key.toLowerCase()) || editedFields.has(key)
        || initialKey !== undefined && JSON.stringify(current) !== JSON.stringify(state.initialBuilt[initialKey]);
      if (changed || parameterKey(state.explicit, key) === undefined && parameterKey(state.inherited, key) === undefined) {
        result = mergeProcessParameters(result, { [key]: current });
      }
    }
    for (const key of Object.keys(state.initialBuilt)) {
      if (parameterKey(built.parameters, key) === undefined) result = mergeProcessParameters(result, { [key]: null });
    }
    for (const field of parameterFieldSchema(processKey) ?? []) {
      if (!editedFields.has(field.name) && !editedFields.has(field.name.toLowerCase())) continue;
      const raw = value.parameterFields[field.name] ?? '';
      if (raw.trim()) continue;
      if (field.name === 'RegionBufferKm') return { ok: false, error: 'FIELDSEG_REGION_BUFFER_INVALID' };
      const cleared = processKey === 'ai.field-segmentation-net' && field.name === 'ModelId' ? null
        : field.type === 'regions' || field.type === 'checks' ? []
        : field.type === 'text' || field.type === 'polygon' ? '' : null;
      result = mergeProcessParameters(result, { [field.name]: cleared });
    }
  }
  for (const [key, current] of Object.entries(result)) {
    if (parameterKey(state.explicit, key) !== undefined) continue;
    const inheritedKey = parameterKey(state.inherited, key);
    if (inheritedKey === undefined) continue;
    const inherited = state.inherited[inheritedKey];
    if (current === '***' && inherited === '***') {
      delete result[key];
    } else if (containsInheritedMask(current, inherited)) {
      return { ok: false, error: 'PROCESS_DEFAULTS_SECRET_REQUIRED' };
    }
  }
  return { ok: true, parameters: result };
}

function containsInheritedMask(value: unknown, inherited: unknown): boolean {
  if (value === '***' && inherited === '***') return true;
  if (!value || !inherited || typeof value !== 'object' || typeof inherited !== 'object') return false;
  if (Array.isArray(value) && Array.isArray(inherited)) return value.some((item, index) => containsInheritedMask(item, inherited[index]));
  if (Array.isArray(value) || Array.isArray(inherited)) return false;
  return Object.entries(value).some(([key, item]) => {
    const inheritedObject = inherited as Record<string, unknown>;
    const previous = parameterKey(inheritedObject, key);
    return previous !== undefined && containsInheritedMask(item, inheritedObject[previous]);
  });
}
